import { FC, useEffect, useRef, useState } from 'react';
import { Crucible } from 'types';
import { Box, Text } from '@chakra-ui/layout';
import { Fade } from '@chakra-ui/react';
import { Spinner } from '@chakra-ui/spinner';
import { AiFillWarning } from 'react-icons/ai';
import { useCrucibleNftData } from 'hooks/query/useCrucibleRenderData';
import crucibleSceneStore from 'helpers/nft/crucibleSceneStore';
import ViewParent from './ViewParent';
import TopBar from './TopBar';
import BottomBar from './BottomBar';
import TraitsTable from './TraitsTable';
import GraphicsOptions from './GraphicsOptions';
import CrucibleMetadata from 'helpers/nft/crucibleScene/crucibleMetadata';
import Controls from './Controls';
import { useWeb3React } from 'hooks/web3';

type Props = {
  crucible: Crucible;
  forceFullscreen?: boolean;
  idTextVisible?: boolean;
  screenshotMode?: boolean;
};

const NFT: FC<Props> = ({
  crucible,
  forceFullscreen,
  idTextVisible = true,
  screenshotMode = false
}) => {
  const { chainId = 1 } = useWeb3React();
  const [isLoading, setIsLoading] = useState(
    (crucibleSceneStore.activeScene &&
      !crucibleSceneStore.activeScene.isLoaded) ||
      false
  );
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [loadingFailed, setLoadingFailed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(forceFullscreen || false);
  const [traitsTable, setTraitsTable] = useState(false);
  const [graphicsOptions, setGraphicsOptions] = useState(false);
  const [toggleControls, setToggleControls] = useState(controlStorage);
  const [loadingMessage, setLoadingMessage] = useState('rendering crucible');
  const [backgroundColor, setBackgroundColor] = useState('');

  const rendererParent = useRef<HTMLDivElement>(null);
  let {
    data: crucibleNftData,
    error: dataError,
    isLoading: dataLoading
  } = useCrucibleNftData(crucible);
  // pass the whole crucible instead of individual variables

  const metadata = new CrucibleMetadata(
    crucible.id,
    crucible.mintBlockNumber,
    crucible.mintTimestamp,
    chainId
  );

  // TODO: "high" texture quality does not exist yet
  crucibleSceneStore.loadScene(metadata, {
    idTextVisible,
    screenshotMode: screenshotMode
  });

  useEffect(() => {
    crucibleSceneStore.activeScene &&
      crucibleSceneStore.activeScene.startRendering();
    crucibleSceneStore.activeScene &&
      setIsLoading(!crucibleSceneStore.activeScene.isLoaded);

    return () => {
      crucibleSceneStore.activeScene &&
        crucibleSceneStore.activeScene.stopRendering();
      setIsLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crucible]);

  useEffect(() => {
    // TODO: Add emitter typings
    crucibleSceneStore.activeScene &&
      crucibleSceneStore.activeScene.ee.on('load', (e) => {
        if (e.type === 'initiated') {
          setIsLoading(true);
        }
        if (e.type === 'completed') {
          setIsLoading(false);
        }
        if (e.type === 'failed') {
          setLoadingFailed(true);
          setLoadingMessage('Something went wrong. Please try reloading!');
          crucibleSceneStore.activeScene &&
            crucibleSceneStore.activeScene.stopRendering();
          if (rendererParent.current) rendererParent.current.innerHTML = '';
        }
      });

    crucibleSceneStore.activeScene &&
      setIsLoading(!crucibleSceneStore.activeScene.isLoaded);
    crucibleSceneStore.activeScene &&
      setBackgroundColor(
        crucibleSceneStore.activeScene.getBackgroundColor(metadata.prng)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crucibleSceneStore.activeScene]);

  useEffect(() => {
    if (rendererParent.current) {
      rendererParent.current.innerHTML = '';
      crucibleSceneStore.activeScene &&
        rendererParent.current.appendChild(
          crucibleSceneStore.activeScene.domElement
        );
      crucibleSceneStore.activeScene &&
        crucibleSceneStore.activeScene.resizeCanvas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rendererParent.current, crucibleNftData, crucible.id]);

  useEffect(() => {
    if (crucibleNftData) {
      crucibleSceneStore.activeScene &&
        crucibleSceneStore.activeScene.update(crucibleNftData, metadata);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crucibleNftData]);

  const checkLoadingManagerProgress =
    crucibleSceneStore.activeScene &&
    crucibleSceneStore.activeScene.crucible.loader.loadingManager.onProgress;

  useEffect(() => {
    if (screenshotMode) {
      if (isLoading === false && crucibleSceneStore.activeScene) {
        crucibleSceneStore.activeScene.crucible.loader.loadingManager.onProgress =
          function (item, loaded, total) {
            if (loaded === total) {
              setIsDataLoading(false);
            }
          };
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkLoadingManagerProgress, isLoading]);

  useEffect(() => {
    crucibleSceneStore.activeScene &&
      crucibleSceneStore.activeScene.resizeCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);
  useEffect(() => {
    if (window === window.parent) {
      localStorage.setItem('crucible-explore--controls', 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleControls]);

  const viewParentProps = isFullscreen
    ? {
        position: 'fixed' as any,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
      }
    : {};

  if (!crucibleSceneStore.activeScene) {
    return <></>;
  }

  return (
    <ViewParent {...viewParentProps}>
      <TopBar
        crucibleId={crucible.id}
        blockNumber={crucible.mintBlockNumber}
        mintTimestamp={crucible.mintTimestamp}
        isLoading={isLoading}
        scene={crucibleSceneStore.activeScene}
        forceFullscreen={!!forceFullscreen}
        screenshotMode={screenshotMode}
        toggleFullscreen={() => setIsFullscreen(!isFullscreen)}
        toggleTraitsTable={() => {
          if (toggleControls) setToggleControls(false);
          setTraitsTable(!traitsTable);
        }}
        toggleGraphicsOptions={() => setGraphicsOptions(!graphicsOptions)}
        toggleControls={() => {
          if (traitsTable) setTraitsTable(false);
          setToggleControls(!toggleControls);
        }}
      />
      {traitsTable && (
        <TraitsTable
          metadata={metadata}
          mintTimestamp={crucible.mintTimestamp}
          blockNumber={crucible.mintBlockNumber}
        />
      )}

      {toggleControls && !screenshotMode && (
        <Controls close={() => setToggleControls(false)} />
      )}
      {graphicsOptions && (
        <GraphicsOptions
          scene={crucibleSceneStore.activeScene}
          handleSetGraphicsOptions={() => setGraphicsOptions(false)}
        />
      )}
      <Box
        className={`nft-box nft-box-${!isDataLoading ? 'loaded' : 'loading'}`}
        ref={rendererParent}
        style={{
          height: isFullscreen ? '100%' : '600px',
          background: backgroundColor
        }}
      />
      <Fade in={isLoading} unmountOnExit>
        <Box
          position='absolute'
          top='50%'
          left={0}
          right={0}
          display='flex'
          flexDir='column'
          alignItems='center'
        >
          {loadingFailed ? <AiFillWarning fontSize='3em' /> : <Spinner />}
          <Text textAlign='center'>{loadingMessage}</Text>
        </Box>
      </Fade>
      {!screenshotMode && (
        <BottomBar
          isLoading={isLoading}
          scene={crucibleSceneStore.activeScene}
          dataError={!!dataError}
          dataLoading={dataLoading}
        />
      )}
    </ViewParent>
  );
};

const controlStorage = () => {
  if (window === window.parent) {
    return localStorage.getItem('crucible-explore--controls') === 'true'
      ? false
      : true;
  }
  return false;
};

export default NFT;
